import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TooltipContentProps } from 'recharts'
import type { WeeklyGmvPoint } from 'core/api/home/weekly-gmv'
import BarLineChart, {
  type BarLineChartLineDotProps,
  type BarLineChartLineSeries
} from 'components/common/charts/bar-line-chart/bar-line-chart'
import WeeklyGmvChartLegend from './weekly-gmv-chart-legend'
import WeeklyGmvChartTooltip from './weekly-gmv-chart-tooltip'
import { fromWeeklyGmvPoints, hasTrajectory, type WeeklyGmvChartDataPoint } from './weekly-gmv-chart-data-point'

interface WeeklyGmvChartProps {
  weeklyGmvPoints: WeeklyGmvPoint[]
  currency: string
}

const REALIZED_COLOR = 'var(--vp-semantic-color-content-brand)'
const FORECAST_COLOR = 'var(--vp-semantic-color-border-neutral)'
const TRAJECTORY_COLOR = 'var(--vp-semantic-color-border-quiet)'
const CURRENT_TRAJECTORY_COLOR = '#E3E6FC'
const BACKGROUND_COLOR = 'var(--vp-semantic-color-content-inverse)'

const renderRealizedDot = ({ cx, cy, payload }: BarLineChartLineDotProps) => {
  if (cx == null || cy == null) return null

  const point = payload as WeeklyGmvChartDataPoint | undefined

  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      stroke={REALIZED_COLOR}
      strokeWidth={2}
      strokeDasharray="none"
      fill={point?.isCurrentWeek ? REALIZED_COLOR : BACKGROUND_COLOR}
    />
  )
}

const renderForecastDot = ({ cx, cy, payload }: BarLineChartLineDotProps) => {
  if (cx == null || cy == null) return null

  const point = payload as WeeklyGmvChartDataPoint | undefined

  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      stroke={point?.isCurrentWeek ? REALIZED_COLOR : FORECAST_COLOR}
      strokeWidth={2}
      strokeDasharray="none"
      fill={point?.isCurrentWeek ? REALIZED_COLOR : BACKGROUND_COLOR}
    />
  )
}

const getWeeklyGmvChartTooltip =
  (
    currency: string,
    realizedTrajectoryLabel: string,
    forecastTrajectoryLabel: string,
    probableTrajectoryLabel: string
  ) =>
  (props: TooltipContentProps<number, string>) => (
    <WeeklyGmvChartTooltip
      {...props}
      currency={currency}
      realizedTrajectoryLabel={realizedTrajectoryLabel}
      forecastTrajectoryLabel={forecastTrajectoryLabel}
      probableTrajectoryLabel={probableTrajectoryLabel}
    />
  )

const WeeklyGmvChart = ({ weeklyGmvPoints, currency }: WeeklyGmvChartProps) => {
  const { t } = useTranslation()
  const trajectoryLabel = t('general.homepage.weekly-gmv-chart.trajectory')
  const realizedLabel = t('general.homepage.weekly-gmv-chart.realized')
  const forecastLabel = t('general.homepage.weekly-gmv-chart.forecast')
  const missingTrajectoryLabel = t('general.homepage.weekly-gmv-chart.missing-trajectory')
  const realizedTrajectoryLabel = t('general.homepage.weekly-gmv-chart.realized-trajectory')
  const forecastTrajectoryLabel = t('general.homepage.weekly-gmv-chart.forecast-trajectory')
  const probableTrajectoryLabel = t('general.homepage.weekly-gmv-chart.probable-trajectory')

  const chartData = useMemo(() => fromWeeklyGmvPoints(weeklyGmvPoints), [weeklyGmvPoints])
  const hasTrajectoryData = hasTrajectory(weeklyGmvPoints)

  const lines: BarLineChartLineSeries[] = [
    {
      dataKey: 'realizedGmvLine',
      name: realizedLabel,
      stroke: REALIZED_COLOR,
      strokeWidth: 2,
      dot: renderRealizedDot
    },
    {
      dataKey: 'forecastGmvLine',
      name: forecastLabel,
      stroke: FORECAST_COLOR,
      strokeDasharray: '2 3',
      strokeWidth: 2,
      dot: renderForecastDot
    }
  ]

  const tooltip = useMemo(
    () => getWeeklyGmvChartTooltip(currency, realizedTrajectoryLabel, forecastTrajectoryLabel, probableTrajectoryLabel),
    [currency, realizedTrajectoryLabel, forecastTrajectoryLabel]
  )

  return (
    <div className="mg-weekly-gmv-chart" data-testid="weekly-gmv-chart">
      <BarLineChart
        data={chartData}
        xKey="label"
        bars={
          hasTrajectoryData
            ? [
                {
                  dataKey: 'trajectoryGmv',
                  name: trajectoryLabel,
                  fill: TRAJECTORY_COLOR,
                  fillOpacity: 0.6,
                  barSize: 34,
                  getCellFill: entry =>
                    (entry as WeeklyGmvChartDataPoint).isCurrentWeek ? CURRENT_TRAJECTORY_COLOR : TRAJECTORY_COLOR
                }
              ]
            : []
        }
        lines={lines}
        tooltip={tooltip}
        xTickClassName="vp-caption"
        legend={
          <WeeklyGmvChartLegend
            trajectoryLabel={trajectoryLabel}
            realizedLabel={realizedLabel}
            forecastLabel={forecastLabel}
            missingTrajectoryLabel={missingTrajectoryLabel}
            hasTrajectory={hasTrajectoryData}
          />
        }
      />
    </div>
  )
}

export default WeeklyGmvChart
